/**
 * Bootstrap queue (`window.kzq`) for deferred init commands and early ad
 * events captured by the publisher stub before `sdk.js` loads.
 *
 * Lifecycle:
 * 1. `adoptKzq()` on SDK attach — run queued functions, hold ad events, take over `push`
 * 2. `closeKzqStub()` when mbp's observers attach — drop the stub; the buffer is
 *    kept, and live events join it via `deferKzqAdEvent`
 * 3. `flushKzqAdBuffer()` on READY — replay once, in order. READY is the earliest
 *    point with a resolved identity to attribute the events to
 * 4. `discardKzqAdBuffer()` — mbp never installed; drop the stub and the buffer
 *
 * Early-exit escape hatch: a page abandoned or backgrounded before READY would
 * take the buffer (and its session) with it, so `armKzqEmergencyFlush` registers
 * a dispatcher that sends whatever is buffered the moment the page hides. The
 * trigger set mirrors yoto-web's `flushAndStopQueue`: `pagehide` where
 * supported (`unload`/`beforeunload` fallback otherwise, to avoid breaking
 * bfcache) plus `visibilitychange` → hidden — mobile pages are routinely killed
 * in background without ever firing `pagehide`. The flush is non-destructive
 * and repeatable: it drains buffered ad events per hide and stays armed, and
 * the READY replay reports whatever buffered after the last drain. Sessions are
 * never part of this path — they stay READY-gated.
 */

export type KzqGptSlotRequestedEntry = {
  source: 'gpt';
  name: 'slotRequested';
  event: unknown;
};

export type KzqGptSlotRenderEndedEntry = {
  source: 'gpt';
  name: 'slotRenderEnded';
  event: unknown;
};

export type KzqPrebidBidWonEntry = {
  source: 'prebid';
  name: 'bidWon';
  event: unknown;
};

export type KzqAdEvent =
  | KzqGptSlotRequestedEntry
  | KzqGptSlotRenderEndedEntry
  | KzqPrebidBidWonEntry;

export type KzqEntry = (() => void) | KzqAdEvent;

export interface KzqQueue {
  push(...items: KzqEntry[]): number;
  teardown?: () => void;
  /**
   * Set when the SDK tears the stub down. Publisher ready-callbacks must check
   * it before attaching — GPT/Prebid may load after teardown.
   */
  closed?: boolean;
  [index: number]: KzqEntry;
  length: number;
}

type KzqWindow = Window & { kzq?: KzqQueue | KzqEntry[] };

let adopted = false;
/** Stub `push` gate — open until `closeKzqStub`. */
let acceptingStubEvents = false;
let stubClosed = false;
/** Once set, events report live instead of buffering. */
let flushed = false;
let bufferedEvents: KzqAdEvent[] = [];
let stubTeardown: (() => void) | undefined;
/** The array adopted in place — publisher stub closures keep referencing it. */
let adoptedQueue: KzqQueue | undefined;

/** Safety cap when init is deferred (e.g. behind consent) and events keep arriving. */
const MAX_BUFFERED_AD_EVENTS = 300;

const isAdEvent = (item: unknown): item is KzqAdEvent => {
  if (!item || typeof item !== 'object') return false;
  const entry = item as Partial<KzqAdEvent>;
  if (!('event' in entry)) return false;
  if (entry.source === 'gpt') {
    return entry.name === 'slotRequested' || entry.name === 'slotRenderEnded';
  }
  return entry.source === 'prebid' && entry.name === 'bidWon';
};

/** The queue the stub actually holds — adoptedQueue survives a `window.kzq` reassign. */
const liveQueue = (): KzqQueue | undefined => {
  if (adoptedQueue) return adoptedQueue;
  const q = (window as KzqWindow).kzq;
  return q && typeof q === 'object' ? (q as KzqQueue) : undefined;
};

const markClosed = (): void => {
  const q = liveQueue();
  if (q) {
    q.closed = true;
  }
};

const callTeardownOnce = (): void => {
  markClosed();
  const teardown = stubTeardown;
  stubTeardown = undefined;
  const q = liveQueue();
  if (q) {
    try {
      delete q.teardown;
    } catch {
      q.teardown = undefined;
    }
  }
  if (typeof teardown !== 'function') return;
  try {
    teardown();
  } catch {
    /* publisher teardown must not break the SDK */
  }
};

const runCommand = (fn: () => void): void => {
  try {
    fn();
  } catch {
    /* ignore */
  }
};

/**
 * Take ownership of `window.kzq`: drain prior entries and override `push`.
 *
 * The publisher's array object is kept — stub closures hold a reference to it
 * (`var q = w.kzq = w.kzq || []`), so its identity must survive adoption.
 */
export function adoptKzq(): void {
  if (typeof window === 'undefined' || adopted) return;
  adopted = true;
  acceptingStubEvents = true;

  const w = window as KzqWindow;
  const existing = w.kzq;
  const prior =
    existing && typeof existing === 'object' ? (existing as KzqQueue) : undefined;
  const queue: KzqQueue = Array.isArray(existing)
    ? (existing as KzqQueue)
    : ([] as unknown as KzqQueue);
  w.kzq = queue;
  adoptedQueue = queue;

  stubTeardown = typeof prior?.teardown === 'function' ? prior.teardown : undefined;
  queue.teardown = stubTeardown;

  bufferedEvents = [];
  const priorItems = (queue as unknown as KzqEntry[]).splice(0, queue.length);

  queue.push = (...items: KzqEntry[]): number => {
    for (const item of items) {
      if (typeof item === 'function') {
        runCommand(item);
      } else if (
        acceptingStubEvents &&
        bufferedEvents.length < MAX_BUFFERED_AD_EVENTS &&
        isAdEvent(item)
      ) {
        bufferedEvents.push(item);
      }
    }
    return queue.length;
  };

  // Ad events must land in the buffer before any command runs — a command may
  // be `init`, and its own synchronous events have to sort after these.
  for (const item of priorItems) {
    if (isAdEvent(item)) {
      bufferedEvents.push(item);
    }
  }
  for (const item of priorItems) {
    if (typeof item === 'function') {
      runCommand(item);
    }
  }
}

/** True after `adoptKzq()` has run. */
export function isKzqAdopted(): boolean {
  return adopted;
}

/** Buffered ad events still waiting for the flush (test/introspection). */
export function getBufferedKzqAdEvents(): readonly KzqAdEvent[] {
  return bufferedEvents;
}

/**
 * Remove the publisher stub's listeners, leaving mbp's observers as the only
 * capture path. The buffer is kept for `flushKzqAdBuffer`.
 */
export function closeKzqStub(): void {
  if (stubClosed) return;
  stubClosed = true;
  acceptingStubEvents = false;
  callTeardownOnce();
}

/**
 * Buffers a live mbp observer event while the flush is still pending. Returns
 * `true` when the event was taken over, meaning the caller must not report it.
 */
export function deferKzqAdEvent(entry: KzqAdEvent): boolean {
  if (flushed) return false;
  if (bufferedEvents.length < MAX_BUFFERED_AD_EVENTS) bufferedEvents.push(entry);
  return true;
}

let emergencyDispatch: ((events: readonly KzqAdEvent[]) => void) | undefined;
let earlyExitArmed = false;

const EARLY_EXIT_LISTEN_OPTS = { capture: true } as const;

/**
 * Drain-and-continue: the buffer is dispatched, but the stub stays attached and
 * keeps capturing — a backgrounded page keeps rendering ads, and only the READY
 * replay or a later hide may report those events. yoto-web can afford the
 * destructive `flushAndStopQueue` (its events carry full ids and go live
 * immediately); this SDK cannot go live without the config, so closing the stub
 * on a temporary hide would lose everything until the config resolves.
 */
const onEarlyExit = (): void => {
  if (flushed || !emergencyDispatch) return;
  const events = bufferedEvents;
  if (events.length === 0) return;
  bufferedEvents = [];
  try {
    emergencyDispatch(events);
  } catch {
    /* the page is going away */
  }
};

const onVisibilityChange = (): void => {
  if (document.visibilityState === 'hidden') onEarlyExit();
};

function disarmEarlyExitListeners(): void {
  window.removeEventListener('pagehide', onEarlyExit, EARLY_EXIT_LISTEN_OPTS);
  window.removeEventListener('unload', onEarlyExit, EARLY_EXIT_LISTEN_OPTS);
  window.removeEventListener('beforeunload', onEarlyExit, EARLY_EXIT_LISTEN_OPTS);
  document.removeEventListener('visibilitychange', onVisibilityChange, EARLY_EXIT_LISTEN_OPTS);
}

/**
 * Register the dispatcher used when the page hides before the READY flush.
 * Re-arming replaces the dispatcher — init arms a minimal pre-state sender,
 * mbp re-arms with the full handlers once they exist.
 */
export function armKzqEmergencyFlush(dispatch: (events: readonly KzqAdEvent[]) => void): void {
  emergencyDispatch = dispatch;
  if (earlyExitArmed || typeof window === 'undefined') return;
  earlyExitArmed = true;
  if ('onpagehide' in window) {
    window.addEventListener('pagehide', onEarlyExit, EARLY_EXIT_LISTEN_OPTS);
  } else {
    // unload/beforeunload only where pagehide is unsupported — they break bfcache.
    const w = window as Window; // 'onpagehide' in window narrows window to never in TS
    w.addEventListener('unload', onEarlyExit, EARLY_EXIT_LISTEN_OPTS);
    w.addEventListener('beforeunload', onEarlyExit, EARLY_EXIT_LISTEN_OPTS);
  }
  document.addEventListener('visibilitychange', onVisibilityChange, EARLY_EXIT_LISTEN_OPTS);
}

/** Replay everything captured so far — stub and live events — once, in order. */
export function flushKzqAdBuffer(replay: (entry: KzqAdEvent) => void): void {
  if (flushed) return;
  flushed = true;
  closeKzqStub();
  const events = bufferedEvents;
  bufferedEvents = [];
  for (const entry of events) {
    try {
      replay(entry);
    } catch {
      /* ignore */
    }
  }
}

/** mbp did not install: remove stub listeners and drop the buffer. */
export function discardKzqAdBuffer(): void {
  if (flushed) return;
  flushed = true;
  bufferedEvents = [];
  closeKzqStub();
}

/** Test-only */
export function _resetKzqForTests(): void {
  adopted = false;
  acceptingStubEvents = false;
  stubClosed = false;
  flushed = false;
  bufferedEvents = [];
  stubTeardown = undefined;
  adoptedQueue = undefined;
  emergencyDispatch = undefined;
  if (earlyExitArmed && typeof window !== 'undefined') {
    disarmEarlyExitListeners();
  }
  earlyExitArmed = false;
  if (typeof window !== 'undefined') {
    try {
      delete (window as KzqWindow).kzq;
    } catch {
      (window as KzqWindow).kzq = undefined;
    }
  }
}
